/**
 * PasskeyReauthButton — "Verify with a passkey" second-factor button for the
 * self-service "Confirm it's you" surfaces (remove device, rotate backup codes,
 * add/remove passkey, add authenticator, change password).
 *
 * Sensitive self-service operations require the current password PLUS a second
 * factor. That second factor is normally an authenticator/backup code, but a
 * passkey-only user has no code to type — so this button runs a fresh passkey
 * assertion (`reauthWithPasskey`) and hands the resulting short-lived
 * `reauth_token` back to the caller, which submits the operation with it.
 *
 * Renders nothing when the browser has no passkey support. The caller keeps the
 * password field and disables this button (via `disabled`) until the other
 * required fields are filled, so activating it authorizes the whole operation.
 */
import { useState } from 'react'
import { isPasskeySupported, reauthWithPasskey } from '../services/passkeys'

interface Props {
  /** Called with the passkey re-auth token; caller runs the op with it. */
  onToken: (token: string) => Promise<void> | void
  /** Surface an error to the caller's error banner. */
  onError: (msg: string) => void
  /** Disable until the caller's other required fields (e.g. password) are ready. */
  disabled?: boolean
  label?: string
  busyLabel?: string
}

export default function PasskeyReauthButton({
  onToken,
  onError,
  disabled,
  label = 'Verify with a passkey',
  busyLabel = 'Waiting for passkey…',
}: Props) {
  const [loading, setLoading] = useState(false)
  if (!isPasskeySupported()) return null

  async function handle() {
    onError('')
    setLoading(true)
    try {
      const result = await reauthWithPasskey()
      await onToken(result.token)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Passkey verification failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="relative my-3">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-2 text-gray-400">or</span>
        </div>
      </div>
      <button
        type="button"
        onClick={handle}
        disabled={disabled || loading}
        className="w-full btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? busyLabel : label}
      </button>
    </div>
  )
}
